import React, { forwardRef } from "react";
import ReactDatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import { tr } from "date-fns/locale";

interface ChakraDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

const CustomInput = forwardRef<
  HTMLInputElement,
  {
    value?: string;
    onClick?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    hasError?: boolean;
    disabled?: boolean;
  }
>(({ value, onClick, placeholder, hasError, disabled }, ref) => (
  <div className="relative w-full">
    <input
      ref={ref}
      value={value || ""}
      onClick={onClick}
      placeholder={placeholder || "Tarih seçiniz"}
      readOnly
      disabled={disabled}
      className={`flex w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition cursor-pointer pr-10
        ${
          hasError
            ? "border-red-500 focus:border-red-500 focus:ring-red-200"
            : "border-gray-300 focus:border-gray-400 focus:ring-gray-200 hover:border-gray-400"
        }
        ${disabled ? "cursor-not-allowed bg-gray-100 opacity-60" : ""}
      `}
    />
    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
      <Calendar className="w-4 h-4 text-gray-400" />
    </div>
  </div>
));

CustomInput.displayName = "CustomInput";

const ChakraDatePicker: React.FC<ChakraDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  hasError = false,
  disabled = false,
  minDate,
  maxDate,
}) => {
  // String value'yu Date'e çevir
  const selectedDate = value ? new Date(value) : null;

  // Date'i string'e çevir (YYYY-MM-DD format)
  const handleChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      onChange(`${year}-${month}-${day}`);
    } else {
      onChange("");
    }
  };

  return (
    <div className="chakra-datepicker">
      <ReactDatePicker
        selected={selectedDate}
        onChange={handleChange}
        dateFormat="dd/MM/yyyy"
        locale={tr}
        placeholderText={placeholder || "Tarih seçiniz"}
        customInput={
          <CustomInput
            hasError={hasError}
            disabled={disabled}
            placeholder={placeholder}
          />
        }
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        isClearable
        popperClassName="chakra-datepicker-popper"
      />
      <style>{`
        .chakra-datepicker-popper {
          z-index: 9999 !important;
        }
        .react-datepicker {
          font-family: inherit;
          border: 1px solid #E2E8F0;
          border-radius: 12px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        .react-datepicker__header {
          background-color: #F7FAFC;
          border-bottom: 1px solid #E2E8F0;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          padding-top: 12px;
        }
        .react-datepicker__current-month {
          font-weight: 600;
          color: #1A202C;
          margin-bottom: 8px;
        }
        .react-datepicker__day-name {
          color: #718096;
          font-weight: 500;
        }
        .react-datepicker__day {
          border-radius: 8px;
          transition: all 0.15s ease;
        }
        .react-datepicker__day:hover {
          background-color: #EDF2F7;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #805AD5 !important;
          color: white !important;
        }
        .react-datepicker__day--selected:hover {
          background-color: #6B46C1 !important;
        }
        .react-datepicker__day--today {
          font-weight: 600;
          color: #805AD5;
        }
        .react-datepicker__day--today.react-datepicker__day--selected {
          color: white;
        }
        .react-datepicker__navigation {
          top: 12px;
        }
        .react-datepicker__navigation-icon::before {
          border-color: #718096;
        }
        .react-datepicker__navigation:hover *::before {
          border-color: #805AD5;
        }
        .react-datepicker__month-dropdown,
        .react-datepicker__year-dropdown {
          background-color: white;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
        }
        .react-datepicker__month-option:hover,
        .react-datepicker__year-option:hover {
          background-color: #EDF2F7;
        }
        .react-datepicker__month-option--selected_month,
        .react-datepicker__year-option--selected_year {
          background-color: #805AD5 !important;
          color: white;
        }
        .react-datepicker__close-icon::after {
          background-color: #805AD5;
        }
        .react-datepicker__triangle {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ChakraDatePicker;
